import constants from '@/constants';
import dayjs from 'dayjs';
import _, { intersection } from 'lodash';
import moment from 'moment';

interface PaginationParams {
  limit: number;
  offset?: number;
  page?: number;
  total?: number;
}

interface PaginationResult {
  page: number;
  limit: number;
  offset: number;
  total?: number;
  totalPage?: number;
}

const queryStringToObject = (url: string) => Object.fromEntries([...new URLSearchParams(url.split('?')[1])]);

const pagination = (params: PaginationParams): PaginationResult => {
  const { limit, offset, page, total } = params;
  const result: PaginationResult = {
    page: 1,
    limit,
    offset: 0,
  };

  if (offset !== undefined) {
    result.offset = offset;
    result.page = Math.floor(offset / limit) + 1;
  } else if (page !== undefined) {
    result.page = page;
    result.offset = (page - 1) * limit;
  }

  if (total !== undefined) {
    result.total = total;
    result.totalPage = Math.ceil(total / limit);
  }

  return result;
};

const getAge = (dateTime: Date | string) => {
  const today = moment();
  const birthday = moment(dateTime);
  let age = '-';
  if (birthday.isValid()) {
    if (today.diff(birthday, 'years')) {
      age = today.diff(birthday, 'years') + ' tuổi';
    } else {
      age = today.diff(birthday, 'month') + ' tháng';
    }
  }
  return age;
};

const getObjIndex = (phr: any, filter?: { tags?: string[]; hasData?: boolean }) => {
  const data: any = {};

  const obj =
    phr?.components?.reduce((data: any, element: any) => {
      data[element.type.code] = element;
      return data;
    }, {}) || {};

  if (window?.emrTypes) {
    for (const code in window?.emrTypes) {
      const type = window?.emrTypes[code];
      const phr = obj[code] || {};
      phr.type = type || phr.type;

      if (filter) {
        if (intersection(filter?.tags, type?.tags)?.length) {
          if (filter?.hasData) {
            if (phr.data) {
              data[code] = phr;
            }
          } else {
            data[code] = phr;
          }
        }
      } else {
        data[code] = phr;
      }
    }
  }

  return data;
};

const safeFormat = (date: string | Date, format?: string, _def?: string) => {
  return date && dayjs(date).isValid() ? dayjs(date).format(format || constants.DATE_FORMAT) : _def || '';
};

const cleanPhone = (phone: string) => {
  const regex = /^(?:\+?84|0)(?:\d){9,10}$/;

  if (regex.test(phone) && phone.startsWith('0')) {
    return '+84' + phone.slice(1);
  }
  return phone;
};

const loading = (show: boolean) => {
  if (show) {
    document.querySelector('.base-loading')?.remove();
    const mainDiv = document.createElement('div');
    mainDiv.className =
      'base-loading fixed top-0 bottom-0 left-0 right-0 z-[9999] bg-white flex flex-col justify-center items-center bg-opacity-60';

    // Tạo ảnh
    const img = document.createElement('img');
    img.src = '/images/logo.png';
    img.className = 'wh-[80px] jump';

    // Tạo đoạn div ngang
    const horizontalDiv = document.createElement('div');
    horizontalDiv.className = 'mt-16 h-[1px] w-16 rounded-full zoom shadow';

    // Thêm các phần tử vào div chính
    mainDiv.appendChild(img);
    mainDiv.appendChild(horizontalDiv);

    // Thêm div chính vào body
    document.body.appendChild(mainDiv);
  } else {
    document.querySelector('.base-loading')?.remove();
  }
};

const filterObj = (obj: any) => {
  return _.pickBy(obj, _.identity);
};

const filterArray = (arr: any[]) => {
  return _.compact(arr);
};

const safeAddress = (address: any) => {
  if (typeof address === 'string') {
    return address;
  }

  if (address?.formatted) {
    return address.formatted;
  }

  if (address?.components?.length) {
    return address.components
      .concat()
      .reverse()
      .filter((e: any) => e)
      .join(', ');
  }

  return address || '';
};

const rangeDateToString = (end: Date, start: Date): string => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);

  if (!startDate.isValid() || !endDate.isValid()) {
    return '';
  }

  const diff = Math.abs(startDate.diff(endDate));
  const diffDuration = dayjs.duration(diff);
  const years = diffDuration.years();
  const months = diffDuration.months();
  const days = diffDuration.days();

  let result = '';

  if (years > 0) {
    result += `${years} năm `;
  }

  if (months > 0) {
    result += `${months} tháng `;
  }

  if (days > 0) {
    result += `${days} ngày`;
  }

  if (result === '') {
    // console.log({ diff, diffDuration, startDate, endDate });
    // debugger;
    result = 'Ngày không hợp lệ';
  }

  return result;
};

const safeValue = (val: any, def: string = '') => {
  if (!val && val !== 0) {
    return def;
  }
  if (typeof val === 'object') {
    return JSON.stringify(val);
  }
  return val;
};

const customSort = (props: {
  arr: any[]; // Mảng cần sắp xếp
  order: any[]; // Thứ tự ưu tiên
  keys: { order: string; time: string }; // Thuộc tính khóa
  sortTime: -1 | 1; // Hướng sắp xếp thời gian (-1: giảm dần, 1: tăng dần)
}) => {
  const { arr, order, keys, sortTime } = props;

  const orderMap: any = {};
  order.forEach((item, index) => {
    orderMap[item] = index;
  });

  arr.sort((a, b) => {
    const aName = keys.order ? _.get(a, keys.order) : undefined; // Trích xuất giá trị thuộc tính order của a
    const bName = keys.order ? _.get(b, keys.order) : undefined; // Trích xuất giá trị thuộc tính order của b
    const aTime = keys.time ? _.get(a, keys.time) : undefined; // Trích xuất giá trị thuộc tính time của a
    const bTime = keys.time ? _.get(b, keys.time) : undefined; // Trích xuất giá trị thuộc tính time của b

    let aIndex = aName !== undefined ? orderMap[aName] : undefined; // Xác định chỉ số ưu tiên của a
    let bIndex = bName !== undefined ? orderMap[bName] : undefined; // Xác định chỉ số ưu tiên của b

    if (aIndex === undefined) aIndex = Infinity; // Nếu không có trong orderMap, gán aIndex = Infinity để đẩy phần tử không có trong order xuống cuối
    if (bIndex === undefined) bIndex = Infinity; // Tương tự cho bIndex

    if (aIndex !== bIndex) {
      return aIndex - bIndex; // Nếu aIndex và bIndex khác nhau, sắp xếp theo thứ tự ưu tiên
    } else if (aTime !== undefined && bTime !== undefined) {
      const aTimeValue = new Date(aTime).getTime(); // Chuyển đổi thời gian thành giá trị số
      const bTimeValue = new Date(bTime).getTime();

      return (aTimeValue - bTimeValue) * sortTime; // Sắp xếp thời gian dựa trên hướng sortTime
    } else {
      return 0; // Trường hợp còn lại, giữ nguyên thứ tự ban đầu
    }
  });

  return arr; // Trả về mảng đã sắp xếp
};

const safeFormatRangeTime = (range: any[], format?: string) => {
  const r = range.filter((date) => moment(date).isValid());
  return r.map((d) => moment(d).format(format || constants.DATE_FORMAT)).join(' - ');
};

const getFiles = (files: string | string[], what?: 'name' | 'name.exten') => {
  let result: string[] = [];
  if (!files) {
    result = [];
  } else if (typeof files === 'string') {
    result = files.split(',');
  } else if (_.isArray(files)) {
    result = files;
  } else {
    result = [];
  }

  if (what) {
    const regex = /\/([^/]+)\.[a-zA-Z0-9]+$/; // Tìm tên file với bất kỳ phần mở rộng nào
    const fileNames = [];
    for (const url of result) {
      const match = url?.match(regex);
      if (match) {
        const fileName = match[what === 'name' ? 1 : 0];
        const decodedFileName = decodeURIComponent(fileName)
          .replace(/^\//, '')
          ?.replace(/^[0-9a-fA-F]+-/, '');
        fileNames.push(decodedFileName);
      }
    }
    return fileNames;
  }

  return result;
};

const getObjFiles = (files: string | string[]) => {
  const result: { url?: string; name?: string; nameExtend?: string }[] = [];

  if ((files && typeof files === 'string') || typeof files?.[0] === 'string') {
    if (typeof files === 'string') {
      files = files.split(',');
    }

    const regex = /\/([^/]+)\.[a-zA-Z0-9]+$/;

    for (const url of files) {
      const obj: any = {};

      const match = url?.match(regex);

      if (match) {
        obj.url = url;

        obj.name = decodeURIComponent(match[1])
          .replace(/^\//, '')
          ?.replace(/^[0-9a-fA-F]+-/, '');
        obj.nameExtend = decodeURIComponent(match[0])
          .replace(/^\//, '')
          ?.replace(/^[0-9a-fA-F]+-/, '');
        result.push(obj);
      }
    }
  }

  return result;
};

function parseLinks(
  linksString: string | string[] = [],
): { name: string; extension: string; url: string; fullName: string }[] {
  const linksArray = typeof linksString === 'string' ? linksString.split(',') : linksString;
  return (
    linksArray?.map?.((link) => {
      const urlParts = link.split('?');
      const filename = decodeURIComponent(urlParts?.[0]?.split('/')?.pop() || '');
      const extension = filename.includes('.') ? filename.split('.').pop() || '' : '';
      return {
        name: filename.replace(/\..*$/, ''),
        fullName: filename,
        extension: extension,
        url: ['dcm', 'dicm'].includes(extension.toLowerCase()) ? encodeURIComponent(link) : link, // Đợi khi nào có bug dicom thì xử lý tiếp
      };
    }) || []
  );
}

const isImgUrl = (url: string) => {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url);
};

const toArray = (val: any, def: any[] = []): any[] => {
  return _.isArray(val) ? val : val ? [val] : def;
};

const parseQueryParams: any = (url: string) => {
  const urlParams = new URLSearchParams(url?.split('?')?.[1]);
  const params: any = {};

  for (const [key, value] of urlParams.entries()) {
    params[key] = value;
  }

  return params;
};

const getObjData = (data: any, route: string, defaultValue?: any) => {
  let value: any;
  console.log(route);
  route.split('.').forEach((k) => {
    value = data?.[k];
  });

  return value || defaultValue;
};

const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

const postMessage = (message: { type: string; data: any }) => {
  const messageJson = JSON.stringify(message);
  // window.postMessage(messageJson);
  if (window?.ReactNativeWebView) {
    window?.ReactNativeWebView.postMessage(messageJson);
  }
};

const isObjectId = (str: string) => {
  // Một ObjectID hợp lệ phải có độ dài 24 ký tự và chỉ chứa các ký tự hexadecimal (0-9 và a-f)
  const regex = /^[0-9a-fA-F]{24}$/;
  return regex.test(str);
};

function removeHTMLTags(str: string) {
  return str?.replace(/<[^>]*>/g, '');
}

const utils = {
  removeHTMLTags,
  isObjectId,
  getObjData,
  queryStringToObject,
  parseLinks,
  parseQueryParams,
  getObjFiles,
  toArray,
  isImgUrl,
  getFiles,
  safeFormatRangeTime,
  customSort,
  safeValue,
  rangeDateToString,
  safeAddress,
  filterArray,
  filterObj,
  pagination,
  getAge,
  getObjIndex,
  safeFormat,
  cleanPhone,
  loading,
  isJsonString,
  postMessage,
};

export default utils;
